
import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { Grid, Typography, CircularProgress, Alert, Button, Box, Snackbar } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Auth as AmplifyAuth } from 'aws-amplify';
import PersonaAvatar from '../PersonaAvatar/PersonaAvatar';
import { Persona, getPersonas, refreshPersonaCache } from '../../lib/api/Persona';
import { useUserInfoContext, UserInfo } from '../../lib/contexts/UserInfoContext';
import { groupPersonasByBaseName } from '../../lib/helpers/personaVariants';
import { canAccessPersona, isSuperAdmin } from '@common/domain/auth';
import { config } from '@config';
import TavusStatusIndicator from '../TavusStatusIndicator/TavusStatusIndicator';
import './NewHomeScreen.css';

interface NewHomeScreenProps {
  createCall?: (persona: Persona) => Promise<string | void>;
  startHairCheck?: (persona: Persona) => void;
  startChat?: (persona: Persona) => void;
}

const PERSONA_LOAD_BUDGET_MS = 30000;

const NewHomeScreen: React.FC<NewHomeScreenProps> = ({ createCall, startHairCheck, startChat }) => {
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingPersona, setLoadingPersona] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'info' | 'error' }>({
    open: false,
    message: '',
    severity: 'info',
  });
  const { userInfo, updateUserInfo } = useUserInfoContext();

  const isMountedRef = useRef(true);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, []);

  const personaGroups = useMemo(() => {
    const safe = Array.isArray(personas) ? personas : [];
    return groupPersonasByBaseName(safe);
  }, [personas]);

  const resolveUser = useCallback(async (): Promise<UserInfo | undefined> => {
    if (userInfo?.accessToken) return userInfo;

    const start = Date.now();
    const timeoutMs = 10000;
    const intervalMs = 500;

    while (Date.now() - start < timeoutMs) {
      try {
        const session = await AmplifyAuth.currentSession();
        const idToken = session.getIdToken();
        const payload: any = idToken.payload;
        return new UserInfo({
          userId: payload["cognito:username"] ?? payload["sub"],
          accessToken: idToken.getJwtToken(),
          email: payload.email,
          name: payload.name,
          groups: payload["cognito:groups"] ?? [],
          userPool: config.userPoolScope,
          isInCall: false,
        });
      } catch {
        // Session not ready yet.
      }

      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }

    return undefined;
  }, [userInfo?.accessToken]);

  const handleRefresh = async () => {
    if (refreshing) return;
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    setRefreshing(true);
    setLoadError(null);

    try {
      const resolvedUser = await resolveUser();
      if (!isMountedRef.current) return;

      if (!resolvedUser?.accessToken) {
        setLoadError('Session expired. Please log in again.');
        setRefreshing(false);
        return;
      }

      let cacheResult: { success: boolean; status?: number; message?: string } | null = null;
      if (isSuperAdmin(resolvedUser.groups)) {
        try {
          cacheResult = await refreshPersonaCache(resolvedUser);
        } catch (cacheErr) {
          console.warn('[NewHomeScreen] Failed to refresh backend persona cache:', cacheErr);
        }
      }

      const response = await getPersonas(resolvedUser);
      if (!isMountedRef.current) return;

      const safePersonas = Array.isArray(response) ? response : [];
      const filtered = safePersonas.filter((p) => canAccessPersona(p, resolvedUser));
      setPersonas(filtered);

      if (!userInfo?.accessToken && resolvedUser) {
        updateUserInfo(resolvedUser);
      }

      if (cacheResult?.status === 429) {
        setSnackbar({
          open: true,
          message: 'Persona cache was refreshed recently; companions reloaded.',
          severity: 'info',
        });
      } else if (cacheResult?.success) {
        setSnackbar({
          open: true,
          message: 'Personas cache refreshed successfully.',
          severity: 'success',
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Companions reloaded.',
          severity: 'success',
        });
      }
    } catch (err) {
      if (!isMountedRef.current) return;
      console.error('[NewHomeScreen] Error refreshing personas:', err);
      setSnackbar({
        open: true,
        message: 'Failed to refresh personas.',
        severity: 'error',
      });
    } finally {
      if (isMountedRef.current) {
        setRefreshing(false);
      }
    }
  };

  useEffect(() => {
    let cancelled = false;
    let retryDelay = 1000;
    const startedAt = Date.now();

    const scheduleRetry = () => {
      if (Date.now() - startedAt >= PERSONA_LOAD_BUDGET_MS) {
        setLoadError('Companions did not load in time. Please retry.');
        setLoading(false);
        return;
      }

      const delay = retryDelay;
      retryDelay = Math.min(retryDelay * 2, 30000);
      retryTimerRef.current = setTimeout(() => {
        if (!cancelled && isMountedRef.current) fetchPersonas();
      }, delay);
    };

    const fetchPersonas = async () => {
      setLoading(true);
      setLoadError(null);

      try {
        const resolvedUser = await resolveUser();
        if (cancelled || !isMountedRef.current) return;

        if (!resolvedUser?.accessToken) {
          setLoading(false);
          scheduleRetry();
          return;
        }

        const response = await getPersonas(resolvedUser);
        if (cancelled || !isMountedRef.current) return;

        const safePersonas = Array.isArray(response) ? response : [];
        const filtered = safePersonas.filter((p) => canAccessPersona(p, resolvedUser));
        setPersonas(filtered);
        setLoading(false);

        if (safePersonas.length === 0) {
          scheduleRetry();
        } else {
          retryDelay = 1000;
          if (!userInfo?.accessToken && resolvedUser) {
            updateUserInfo(resolvedUser);
          }
        }
      } catch (error) {
        if (cancelled || !isMountedRef.current) return;
        console.error('Error fetching personas:', error);
        setPersonas([]);
        setLoading(false);
        scheduleRetry();
      }
    };

    fetchPersonas();
    return () => {
      cancelled = true;
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.accessToken, updateUserInfo, reloadKey, resolveUser]);

  return (
    <div className="new-home-screen">
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          position: { sm: 'absolute' },
          top: { sm: 'var(--space-md, 16px)' },
          right: { sm: 'var(--space-lg, 24px)' },
          mb: { xs: 1.5, sm: 0 },
          zIndex: 10,
        }}
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={refreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
          disabled={refreshing || loading}
          onClick={handleRefresh}
          aria-label="Refresh personas"
          data-testid="refresh-personas-button"
          sx={{
            textTransform: 'none',
            borderRadius: '20px',
            borderColor: 'var(--glass-border-subtle, rgba(0, 0, 0, 0.23))',
            color: 'var(--color-text, inherit)',
            '&:hover': {
              borderColor: 'var(--color-accent, #b8860b)',
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </Button>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          mb: 1,
        }}
      >
        <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
          Choose Your Companion
        </Typography>
        <TavusStatusIndicator accessToken={userInfo?.accessToken} />
      </Box>
      {loading ? (
        <CircularProgress />
      ) : loadError ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', maxWidth: 420, mx: 'auto' }}>
          <Alert severity="error" sx={{ width: '100%' }}>
            {loadError}
          </Alert>
          <Button
            variant="contained"
            onClick={handleRefresh}
            disabled={refreshing}
            startIcon={refreshing ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {refreshing ? 'Refreshing…' : 'Retry Loading Companions'}
          </Button>
        </Box>
      ) : personaGroups.length === 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', maxWidth: 420, mx: 'auto' }}>
          <Alert severity="warning" sx={{ width: '100%' }}>
            No companions are currently available.
          </Alert>
          <Button
            variant="contained"
            onClick={handleRefresh}
            disabled={refreshing}
            startIcon={refreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
          >
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3} className="persona-grid">
          {personaGroups.map((group, index) => {
            // Render ONE card per base persona (e.g. "Caesar" instead of "Caesar v1" + "Caesar v2")
            // Use the highest version as the primary display variant, but show the base name.
            const displayPersona: Persona = { ...group.primary, name: group.baseName };
            const loadingKey = group.primary.id ?? group.baseName;

            return (
            <Grid item key={group.baseName} xs={12} sm={6} md={4} lg={3}>
              <div onClick={async () => {
                if (startChat) {
                  setLoadingPersona(loadingKey);
                  startChat(group.primary);
                  setLoadingPersona(null);
                } else if (startHairCheck) {
                  setLoadingPersona(loadingKey);
                  await startHairCheck(group.primary);
                  setLoadingPersona(null);
                }
              }}>
                <PersonaAvatar
                  persona={displayPersona}
                  index={index}
                  loading={loadingPersona === loadingKey}
                />
              </div>
            </Grid>
          )})}
        </Grid>
      )}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default NewHomeScreen;
